import {
  Alert,
  Button,
  Checkbox,
  Divider,
  Icons,
  iconSize,
  Stack,
  TextField,
  Typography,
} from '@munifocus/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ChecklistSnapshotSummary } from '@munifocus/contracts-gov';
import {
  useCaptureChecklistSnapshot,
  useChecklistSnapshot,
  useChecklistSnapshots,
} from '../lib/checklist-api';

/**
 * Frozen versions of the Step 5.1 legal checklist.
 *
 * The checklist itself derives its auto items from the file as it stands, which
 * is right while the officer is working and wrong afterwards: the file keeps
 * moving, so the same checklist read at an appeal reports today's file rather
 * than the state the notice issued on. A version is the record of what it said
 * at the moment of a decision.
 *
 * Read-only by construction. There is no edit or delete — a record that could be
 * revised after the fact would not be worth keeping.
 */

/** Matches `CaptureChecklistSnapshotDto`'s `@MaxLength(200)` and the column. */
const LABEL_MAX_LENGTH = 200;

function VersionItems({ fileId, version }: { fileId: string; version: number }) {
  const { t } = useTranslation();
  const { data, isError } = useChecklistSnapshot(fileId, version);

  if (isError) return <Alert severity="error">{t('gov.checklist.versions.loadError')}</Alert>;
  if (!data) return null;

  return (
    <Stack spacing={0.5} sx={{ pl: 2, pb: 1 }}>
      <Alert severity="info" sx={{ py: 0 }}>
        {t('gov.checklist.versions.viewing', { version })}
      </Alert>
      {data.items.map((item) => (
        <Stack key={item.key} direction="row" sx={{ alignItems: 'flex-start', gap: 1 }}>
          {/* Disabled rather than plain text: the tick is the record, and it
              should read as one to a screen reader too. */}
          <Checkbox size="small" checked={item.checked} disabled sx={{ mt: -0.5 }} />
          <Stack sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2">{t(`gov.checklist.items.${item.key}`)}</Typography>
            {item.note && (
              <Typography variant="caption" color="text.secondary">
                {item.note}
              </Typography>
            )}
            {item.itemDate && (
              <Typography variant="caption" color="text.secondary">
                {item.itemDate}
              </Typography>
            )}
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
}

function VersionRow({ snapshot, fileId }: { snapshot: ChecklistSnapshotSummary; fileId: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <Stack spacing={0.5} sx={{ py: 1 }}>
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="body2">
          {t('gov.checklist.versions.version', { version: snapshot.version })}
        </Typography>
        {/* Not a StatusChip: the capture reason is not a status, and every
            available chip kind (pending / approved / warning …) would colour it
            with a meaning it does not carry. */}
        <Typography variant="caption" color="text.secondary">
          {t(`gov.checklist.versions.reason.${snapshot.reason}`)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t('gov.checklist.versions.summary', {
            done: snapshot.checkedCount,
            total: snapshot.itemCount,
          })}
        </Typography>
        <Button size="small" sx={{ ml: 'auto' }} onClick={() => setOpen((v) => !v)}>
          {t(open ? 'gov.checklist.versions.hide' : 'gov.checklist.versions.view')}
        </Button>
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {snapshot.capturedAt.slice(0, 10)}
        {snapshot.capturedByName
          ? ` · ${t('gov.checklist.versions.by', { name: snapshot.capturedByName })}`
          : ''}
        {snapshot.label ? ` · ${snapshot.label}` : ''}
      </Typography>
      {open && <VersionItems fileId={fileId} version={snapshot.version} />}
    </Stack>
  );
}

export function ChecklistVersions({ fileId }: { fileId: string }) {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useChecklistSnapshots(fileId);
  const capture = useCaptureChecklistSnapshot(fileId);
  const [label, setLabel] = useState('');

  const snapshots = data ?? [];

  return (
    <Stack spacing={1} sx={{ mt: 2 }}>
      <Divider />
      <Typography variant="overline">{t('gov.checklist.versions.title')}</Typography>
      <Typography variant="caption" color="text.secondary">
        {t('gov.checklist.versions.hint')}
      </Typography>

      {isError && <Alert severity="error">{t('gov.checklist.versions.loadError')}</Alert>}

      {/* Gated on `isLoading` too: `data` is undefined while the query is in
          flight, so without it the screen briefly claims no versions exist on a
          file that has them. */}
      {snapshots.length === 0 && !isError && !isLoading && (
        <Typography variant="body2" color="text.secondary">
          {t('gov.checklist.versions.empty')}
        </Typography>
      )}

      {snapshots.length > 0 && (
        <Stack divider={<Divider flexItem />}>
          {snapshots.map((s) => (
            <VersionRow key={s.id} snapshot={s} fileId={fileId} />
          ))}
        </Stack>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: 'flex-start' }}>
        <TextField
          size="small"
          fullWidth
          label={t('gov.checklist.versions.labelField')}
          // The DTO and the column both cap this at 200; enforcing it here turns
          // an avoidable 400 into an input that simply stops accepting more.
          slotProps={{ htmlInput: { maxLength: LABEL_MAX_LENGTH } }}
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />
        <Button
          size="small"
          startIcon={<Icons.Camera size={iconSize.md} />}
          disabled={capture.isPending}
          onClick={() =>
            capture.mutate({ label: label.trim() || undefined }, { onSuccess: () => setLabel('') })
          }
        >
          {t('gov.checklist.versions.capture')}
        </Button>
      </Stack>
    </Stack>
  );
}
